import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Gamepad2, Plus, Edit, Trash2, Power, Wrench, RefreshCw,
  Eye, BarChart3, Settings, ChevronDown, Save, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';

const GameManager = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    game_type: 'custom',
    thumbnail: '',
    game_url: '',
    instructions: '',
    controls: ''
  });

  useEffect(() => {
    fetchGames();
    fetchAnalytics();
  }, []);

  const fetchGames = async () => {
    try {
      const res = await api.get('/admin/games?include_inactive=true');
      setGames(res.data.games || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/admin/games/analytics/overview');
      setAnalytics(res.data.analytics);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.slug || !form.description) {
      toast({ title: "Error", description: "Fill required fields", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      if (editingGame) {
        await api.put(`/admin/games/${editingGame.game_id}`, form);
        toast({ title: "Success", description: "Game updated" });
      } else {
        await api.post('/admin/games', form);
        toast({ title: "Success", description: "Game created" });
      }
      
      setShowForm(false);
      setEditingGame(null);
      resetForm();
      fetchGames();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save game",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setForm({
      name: game.name,
      slug: game.slug,
      description: game.description,
      game_type: game.type,
      thumbnail: game.thumbnail || '',
      game_url: game.game_url || '',
      instructions: game.instructions || '',
      controls: game.controls || ''
    });
    setShowForm(true);
  };

  const handleToggleStatus = async (game, field) => {
    try {
      const update = {};
      if (field === 'active') {
        update.is_active = !game.is_active;
      } else if (field === 'maintenance') {
        update.is_maintenance = !game.is_maintenance;
      }
      
      await api.patch(`/admin/games/${game.game_id}/status`, update);
      toast({ title: "Success", description: "Status updated" });
      fetchGames();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (game) => {
    if (!confirm(`Delete "${game.name}"? This will deactivate the game.`)) return;
    
    try {
      await api.delete(`/admin/games/${game.game_id}`);
      toast({ title: "Success", description: "Game deleted" });
      fetchGames();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      slug: '',
      description: '',
      game_type: 'custom',
      thumbnail: '',
      game_url: '',
      instructions: '',
      controls: ''
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const getGameTypeIcon = (type) => {
    switch (type) {
      case 'chess': return '♟️';
      case 'platformer': return '🦘';
      case 'puzzle': return '🧩';
      case 'runner': return '🏃';
      default: return '🎮';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="game-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gamepad2 className="w-7 h-7" />
            Game Manager
          </h1>
          <p className="text-muted-foreground text-sm">Manage games in the entertainment zone</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/game-wallets')}>
            <BarChart3 className="w-4 h-4 mr-2" />
            System Wallets
          </Button>
          <Button onClick={() => { resetForm(); setEditingGame(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Game
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{analytics.total_games}</p>
              <p className="text-xs text-muted-foreground">Active Games</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{analytics.total_sessions}</p>
              <p className="text-xs text-muted-foreground">Total Sessions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{analytics.total_entry_fees?.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Entry Fees (AGP)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{analytics.total_rewards_given?.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Rewards Given (AGP)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Games List */}
      <Card>
        <CardHeader>
          <CardTitle>Games ({games.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {games.length === 0 ? (
            <div className="text-center py-12">
              <Gamepad2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No games yet</p>
              <Button className="mt-4" onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Game
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {games.map((game) => (
                <div 
                  key={game.game_id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    !game.is_active ? 'opacity-50 bg-muted/50' : 'bg-muted/30'
                  }`}
                  data-testid={`game-item-${game.slug}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-2xl">
                      {game.thumbnail ? (
                        <img 
                          src={getImageUrl(game.thumbnail)} 
                          alt={game.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        getGameTypeIcon(game.type)
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold flex items-center gap-2">
                        {game.name}
                        {game.is_maintenance && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                            Maintenance
                          </span>
                        )}
                        {!game.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">
                            Inactive
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground">{game.description?.substring(0, 60)}...</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {game.play_count || 0} plays | Fees: {game.total_entry_fees?.toFixed(0) || 0} AGP
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleToggleStatus(game, 'maintenance')}
                      title={game.is_maintenance ? 'End Maintenance' : 'Set Maintenance'}
                    >
                      <Wrench className={`w-4 h-4 ${game.is_maintenance ? 'text-yellow-400' : ''}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleToggleStatus(game, 'active')}
                      title={game.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <Power className={`w-4 h-4 ${game.is_active ? 'text-green-400' : 'text-red-400'}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEdit(game)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(game)}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Game Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingGame ? 'Edit Game' : 'Add New Game'}</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingGame(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Game Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => {
                        setForm({ 
                          ...form, 
                          name: e.target.value,
                          slug: editingGame ? form.slug : generateSlug(e.target.value)
                        });
                      }}
                      placeholder="Chess Master"
                      required
                    />
                  </div>
                  <div>
                    <Label>Slug *</Label>
                    <Input
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      placeholder="chess-master"
                      required
                      disabled={!!editingGame}
                    />
                  </div>
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="A fun chess game..."
                    rows={2}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Game Type</Label>
                    <select
                      value={form.game_type}
                      onChange={(e) => setForm({ ...form, game_type: e.target.value })}
                      className="w-full px-3 py-2 bg-background border rounded-md"
                    >
                      <option value="chess">Chess</option>
                      <option value="platformer">Platformer (Mario-style)</option>
                      <option value="puzzle">Puzzle (Match-3)</option>
                      <option value="runner">Runner (Infinite)</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <Label>Thumbnail URL</Label>
                    <Input
                      value={form.thumbnail}
                      onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <Label>Game URL (Hosted game embed URL)</Label>
                  <Input
                    value={form.game_url}
                    onChange={(e) => setForm({ ...form, game_url: e.target.value })}
                    placeholder="https://cdn.example.com/games/chess/index.html"
                  />
                </div>

                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    value={form.instructions}
                    onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                    placeholder="How to play..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Controls</Label>
                  <Input
                    value={form.controls}
                    onChange={(e) => setForm({ ...form, controls: e.target.value })}
                    placeholder="Arrow keys to move, Space to jump"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowForm(false); setEditingGame(null); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={saving}>
                    {saving ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {editingGame ? 'Update Game' : 'Create Game'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GameManager;
